// ============================================================
// CLUBHUB – Spenden: aus Datensaetzen werden Ansichten
// ============================================================
// Reine Funktionen ohne Datenbank und ohne Server-Importe: sie
// laufen ueber dem Musterbestand wie ueber dem, was Prisma liefert
// (beides in der Form von `saetze.ts`). Hier steht auch die Fachregel, wer wo
// GENANNT werden darf:
//
//   Liste, Aufruf, Website   anonym bleibt anonym — der Name steht
//                            nur, wenn die Spende nicht anonym ist;
//                            auf der Website zusaetzlich nur mit
//                            Einwilligung „Internet“ (consentInternet)
//   Bestätigung, Spender     der hinterlegte Name — sonst bekaeme
//                            die Person keine Bestätigung
// ============================================================
import type { ContactView } from "@/lib/views";
import type { AufrufSatz, BestaetigungSatz, SpendeSatz } from "./saetze";
import { berlinTag, jahrVon, tageZwischen } from "./format";
import type {
  Anschrift,
  AufrufPhase,
  AufrufView,
  Belegform,
  BestaetigungView,
  JahresSumme,
  Kandidat,
  Sachangaben,
  SpendeView,
  SpenderView,
  Verzichtsangaben,
} from "./views";

// ── Personen ─────────────────────────────────────────────────

/** Was eine Anschrift von der Person braucht — `ContactView` oder eine Zeile aus der Datenbank. */
export type AnschriftQuelle = Pick<ContactView, "kind" | "title" | "firstName" | "lastName" | "organizationName" | "displayName" | "street" | "zip" | "city" | "country">;

/** „Dr. Lena Huber“ bzw. der Firmenname — so steht die Person in einer Anschrift. */
export function vollerName(c: AnschriftQuelle): string {
  if (c.kind !== "PERSON" && c.organizationName) return c.organizationName;
  return [c.title, c.firstName, c.lastName].filter(Boolean).join(" ") || c.displayName;
}

export function anschriftVon(c: AnschriftQuelle): Anschrift {
  const ort = [c.zip, c.city].filter(Boolean).join(" ");
  return {
    name: vollerName(c),
    zeilen: [c.street, ort, c.country && c.country !== "DE" ? c.country : null].filter((z): z is string => Boolean(z)),
  };
}

/** Name UND Anschrift sind Pflicht auf dem amtlichen Muster. */
export function adresseVollstaendig(c: Pick<ContactView, "street" | "zip" | "city">): boolean {
  return Boolean(c.street && c.zip && c.city);
}

/** Wie die Bestätigung hinausgeht: Mail, wenn die Person Mail will und eine Adresse hat. */
export function versandweg(c: ContactView): "EMAIL" | "POST" {
  return c.communication === "EMAIL" && c.email ? "EMAIL" : "POST";
}

// ── Aufrufe ──────────────────────────────────────────────────

export function phaseVon(a: Pick<AufrufSatz, "startsAt" | "endsAt" | "isActive">, heute: string): AufrufPhase {
  if (!a.isActive) return "BEENDET";
  if (a.endsAt && a.endsAt < heute) return "BEENDET";
  if (a.startsAt && a.startsAt > heute) return "GEPLANT";
  return "LAUFEND";
}

export function aufrufView(a: AufrufSatz, spenden: SpendeSatz[], heute: string): AufrufView {
  const eigene = spenden.filter((s) => s.campaignId === a.id);
  const raised = runden(eigene.reduce((s, d) => s + d.amount, 0));
  const spender = new Set(eigene.map((d) => d.contactId ?? `anonym-${d.id}`));
  const phase = phaseVon(a, heute);
  return {
    ...a,
    phase,
    raised,
    donationCount: eigene.length,
    donorCount: spender.size,
    progress: a.goalAmount ? Math.round((raised / a.goalAmount) * 1000) / 10 : null,
    tage:
      phase === "LAUFEND" && a.endsAt
        ? tageZwischen(heute, a.endsAt)
        : phase === "GEPLANT" && a.startsAt
          ? tageZwischen(heute, a.startsAt)
          : null,
  };
}

// ── Spenden ──────────────────────────────────────────────────

export function spendeView(
  s: SpendeSatz,
  kontakte: Map<string, ContactView>,
  aufrufe: Map<string, AufrufSatz>,
  bestaetigungen: Map<string, BestaetigungSatz>,
): SpendeView {
  const c = s.contactId ? kontakte.get(s.contactId) : undefined;
  const anonym = s.isAnonymous || !c;
  const details = s.inKindDetails;
  return {
    id: s.id,
    contactId: s.contactId,
    spenderName: anonym ? null : c.displayName,
    hinterlegterName: c ? c.displayName : null,
    spenderKind: c?.kind ?? null,
    isAnonymous: anonym,
    namentlichOeffentlich: !anonym && c.consentInternet,
    campaignId: s.campaignId,
    campaignTitle: s.campaignId ? (aufrufe.get(s.campaignId)?.title ?? null) : null,
    kind: s.kind,
    amount: s.amount,
    date: s.date,
    method: s.method,
    purpose: s.purpose,
    sach: s.kind === "IN_KIND" && details?.art === "SACH" ? (details as Sachangaben) : null,
    verzicht: s.kind === "WAIVER" && details?.art === "VERZICHT" ? (details as Verzichtsangaben) : null,
    receiptId: s.receiptId,
    receiptNumber: s.receiptId ? (bestaetigungen.get(s.receiptId)?.number ?? null) : null,
    note: s.note,
    createdAt: s.createdAt,
  };
}

/** Neueste zuerst, bei gleichem Tag die zuletzt erfasste. */
export function nachDatum(a: { date: string; id: string }, b: { date: string; id: string }): number {
  return b.date.localeCompare(a.date) || b.id.localeCompare(a.id);
}

// ── Bestätigungen ────────────────────────────────────────────

export function belegform(b: Pick<BestaetigungSatz, "isCollective" | "kind">): Belegform {
  if (b.isCollective) return "SAMMEL";
  return b.kind === "IN_KIND" ? "SACH" : "GELD";
}

export function bestaetigungView(b: BestaetigungSatz, alle: BestaetigungSatz[]): BestaetigungView {
  const ersetzt = b.replacesId ? alle.find((x) => x.id === b.replacesId) : undefined;
  const nachfolger = alle.find((x) => x.replacesId === b.id);
  return {
    id: b.id,
    number: b.number,
    contactId: b.contactId,
    empfaenger: b.snapshot.empfaenger,
    isCollective: b.isCollective,
    form: belegform(b),
    periodFrom: b.periodFrom,
    periodTo: b.periodTo,
    totalAmount: b.totalAmount,
    kind: b.kind,
    issuedAt: b.issuedAt,
    signedBy: b.signedBy,
    aussteller: b.snapshot.aussteller,
    posten: b.snapshot.posten,
    sach: b.snapshot.sach,
    ausgestelltAm: b.snapshot.ausgestelltAm ?? berlinTag(b.issuedAt),
    hatPdf: Boolean(b.pdfKey),
    sentAt: b.sentAt,
    sentVia: b.sentVia,
    cancelledAt: b.cancelledAt,
    cancelReason: b.cancelReason,
    replacesNumber: ersetzt?.number ?? null,
    replacedByNumber: nachfolger?.number ?? null,
    replacedById: nachfolger?.id ?? null,
  };
}

// ── Summen ───────────────────────────────────────────────────

export function runden(betrag: number): number {
  return Math.round(betrag * 100) / 100;
}

export function summe(spenden: { amount: number }[]): number {
  return runden(spenden.reduce((s, d) => s + d.amount, 0));
}

export function jahresSummen(spenden: SpendeView[]): JahresSumme[] {
  const je = new Map<number, JahresSumme>();
  for (const s of spenden) {
    const jahr = jahrVon(s.date);
    const j = je.get(jahr) ?? { jahr, anzahl: 0, summe: 0, geld: 0, sach: 0, verzicht: 0, bestaetigt: 0 };
    j.anzahl += 1;
    j.summe = runden(j.summe + s.amount);
    if (s.kind === "MONEY") j.geld = runden(j.geld + s.amount);
    if (s.kind === "IN_KIND") j.sach = runden(j.sach + s.amount);
    if (s.kind === "WAIVER") j.verzicht = runden(j.verzicht + s.amount);
    if (s.receiptId) j.bestaetigt = runden(j.bestaetigt + s.amount);
    je.set(jahr, j);
  }
  return [...je.values()].sort((a, b) => b.jahr - a.jahr);
}

// ── Spender ──────────────────────────────────────────────────

export function spenderListe(spenden: SpendeView[], kontakte: Map<string, ContactView>, jahr: number): SpenderView[] {
  const je = new Map<string, SpendeView[]>();
  for (const s of spenden) {
    if (!s.contactId) continue;
    je.set(s.contactId, [...(je.get(s.contactId) ?? []), s]);
  }
  const liste: SpenderView[] = [];
  for (const [contactId, eigene] of je) {
    const c = kontakte.get(contactId);
    if (!c) continue;
    const daten = eigene.map((s) => s.date).sort();
    const offen = eigene.filter((s) => !s.receiptId);
    liste.push({
      id: contactId,
      contactId,
      name: c.displayName,
      kind: c.kind,
      status: c.status,
      memberNumber: c.memberNumber,
      anschrift: anschriftVon(c),
      adresseVollstaendig: adresseVollstaendig(c),
      email: c.email,
      communication: c.communication,
      consentInternet: c.consentInternet,
      wuenschtAnonym: eigene.some((s) => s.isAnonymous),
      anzahl: eigene.length,
      summe: summe(eigene),
      summeJahr: summe(eigene.filter((s) => jahrVon(s.date) === jahr)),
      summeVorjahr: summe(eigene.filter((s) => jahrVon(s.date) === jahr - 1)),
      erste: daten[0]!,
      letzte: daten[daten.length - 1]!,
      offen: offen.length,
      offenBetrag: summe(offen),
    });
  }
  return liste;
}

/**
 * Wer im Jahr `jahr` Spenden ohne Bestätigung hat — die Grundlage
 * des Assistenten. Ohne Person (Spendenbox) gibt es niemanden, dem
 * man etwas bestätigen koennte; anonym Genannte bekommen ihre
 * Bestätigung trotzdem.
 */
export function kandidaten(spenden: SpendeView[], kontakte: Map<string, ContactView>, jahr: number): Kandidat[] {
  const je = new Map<string, SpendeView[]>();
  for (const s of spenden) {
    if (!s.contactId || s.receiptId || jahrVon(s.date) !== jahr) continue;
    je.set(s.contactId, [...(je.get(s.contactId) ?? []), s]);
  }
  const liste: Kandidat[] = [];
  for (const [contactId, eigene] of je) {
    const c = kontakte.get(contactId);
    if (!c) continue;
    const sortiert = [...eigene].sort((a, b) => a.date.localeCompare(b.date));
    liste.push({
      contactId,
      name: c.displayName,
      anschrift: anschriftVon(c),
      adresseVollstaendig: adresseVollstaendig(c),
      email: c.email,
      versand: versandweg(c),
      spenden: sortiert,
      summe: summe(sortiert),
      geld: sortiert.filter((s) => s.kind !== "IN_KIND"),
      sach: sortiert.filter((s) => s.kind === "IN_KIND"),
    });
  }
  return liste.sort((a, b) => a.name.localeCompare(b.name, "de"));
}

/** Jahre mit unbestätigten Spenden bekannter Personen, mit Anzahl der Personen. */
export function offeneJahre(spenden: SpendeView[]): { jahr: number; personen: number; spenden: number }[] {
  const je = new Map<number, { personen: Set<string>; spenden: number }>();
  for (const s of spenden) {
    if (!s.contactId || s.receiptId) continue;
    const jahr = jahrVon(s.date);
    const j = je.get(jahr) ?? { personen: new Set<string>(), spenden: 0 };
    j.personen.add(s.contactId);
    j.spenden += 1;
    je.set(jahr, j);
  }
  return [...je.entries()]
    .map(([jahr, j]) => ({ jahr, personen: j.personen.size, spenden: j.spenden }))
    .sort((a, b) => b.jahr - a.jahr);
}

// ── Wann eine Bestätigung Pflicht ist ────────────────────────

/** Zahlungswege, fuer die es eine Buchungsbestätigung der Bank bzw. des Zahlungsdienstes gibt. */
const MIT_BUCHUNGSBELEG = new Set(["TRANSFER", "SEPA", "PAYPAL", "CARD"]);

/**
 * Genuegt dem Finanzamt der Kontoauszug (vereinfachter Nachweis,
 * § 50 Abs. 4 EStDV)? Nur bei Geld, das ueber ein Konto kam, und nur
 * bis zur Grenze JE ZUWENDUNG (`lib/rechtswerte`). Bar, Sachspende
 * und Aufwandsverzicht brauchen immer eine Bestätigung.
 */
export function kontoauszugGenuegt(s: Pick<SpendeView, "kind" | "method" | "amount">, grenze: number): boolean {
  return s.kind === "MONEY" && MIT_BUCHUNGSBELEG.has(s.method) && s.amount <= grenze;
}
